import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box, Button, Container, Paper, Stack, TextField, Toolbar, Typography,
} from '@mui/material';
import { CheckCircleOutline } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import SurveyPublicService from '../../../shared/services/survey/survey-public.service';
import { SurveyProductDataStep } from './survey-product-data-step';
import { LoadingSpinner } from '../../presentation-components/display-elements-components/loading-spinner';
import { CustomThemeContext } from '../../../context/theme/custom-theme-context';
import { QuestionInput } from '../../presentation-components/surveys/respond/question-inputs';
import { evaluateRule, isItemAnswered, isItemInRange, isItemRequired } from '../../presentation-components/surveys/respond/respond-utils';
import {
  ISurveyQuestionGroupItem,
  ISurveyResolveResponse,
  ISurveyResponseItem,
} from '../../../shared/services/survey/survey.model';

type Phase = 'loading' | 'error' | 'identity' | 'questions' | 'submitted' | 'already_submitted';

const AUTOSAVE_DELAY_MS = 1000;

export const SurveyRespondPage = () => {
  const [searchParams] = useSearchParams();
  const surveyPublicService = useRef(new SurveyPublicService()).current;
  const customThemeContext = useContext(CustomThemeContext);

  const inviteToken = searchParams.get('inviteToken') ?? undefined;
  const openLinkToken = searchParams.get('openLinkToken') ?? undefined;

  const [phase, setPhase] = useState<Phase>('loading');
  const [errorMessage, setErrorMessage] = useState('');
  const [surveyData, setSurveyData] = useState<ISurveyResolveResponse | null>(null);
  const [answers, setAnswers] = useState<Map<string, ISurveyResponseItem>>(new Map());
  const [sessionToken, setSessionToken] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [showValidation, setShowValidation] = useState(false);
  const [saveState, setSaveState] = useState<'idle' | 'saving' | 'saved'>('idle');
  const [resumedProductRows, setResumedProductRows] = useState<{ capitalGoodId: string; unitAmount: number; measurementUnitId: string; kgCarbonEmission: number }[]>([]);

  // Identity fields (open link)
  const [respondentEmail, setRespondentEmail] = useState('');
  const [respondentName, setRespondentName] = useState('');

  // ----- Session persistence: progress survives a closed tab -----
  // Anonymous surveys use sessionStorage so a shared machine never resurrects
  // another person's answers after the tab closes; identified surveys may use
  // localStorage and survive a browser restart.
  const accessToken = inviteToken ?? openLinkToken ?? '';
  const storageKey = `survey-session:${accessToken}`;
  const storageFor = (anonymous: boolean): Storage => (anonymous ? window.sessionStorage : window.localStorage);

  const readStoredToken = (anonymous: boolean): string | null => {
    try {
      return storageFor(anonymous).getItem(storageKey);
    } catch {
      return null;
    }
  };
  const writeStoredToken = (anonymous: boolean, token: string) => {
    try {
      storageFor(anonymous).setItem(storageKey, token);
    } catch { /* storage unavailable; resume is best effort */ }
  };
  const clearStoredToken = () => {
    try {
      window.sessionStorage.removeItem(storageKey);
      window.localStorage.removeItem(storageKey);
    } catch { /* ignore */ }
  };

  // ----- Autosave: answers post as the respondent types, so nothing is lost -----
  const dirtyQuestionIdsRef = useRef<Set<string>>(new Set());
  const answersRef = useRef(answers);
  answersRef.current = answers;
  const sessionTokenRef = useRef(sessionToken);
  sessionTokenRef.current = sessionToken;
  const phaseRef = useRef(phase);
  phaseRef.current = phase;
  const autosaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const flushAnswers = useCallback(async () => {
    if (autosaveTimerRef.current) {
      clearTimeout(autosaveTimerRef.current);
      autosaveTimerRef.current = null;
    }
    const token = sessionTokenRef.current;
    if (!token || phaseRef.current !== 'questions') return;
    const dirtyIds = [...dirtyQuestionIdsRef.current];
    if (dirtyIds.length === 0) return;
    const items = dirtyIds
      .map(id => answersRef.current.get(id))
      .filter((a): a is ISurveyResponseItem => Boolean(a));
    if (items.length === 0) { dirtyQuestionIdsRef.current.clear(); return; }
    dirtyQuestionIdsRef.current.clear();
    setSaveState('saving');
    try {
      await surveyPublicService.saveAnswers(token, items);
      setSaveState('saved');
    } catch {
      // Put the ids back so the next change or the final submit retries them.
      for (const id of dirtyIds) dirtyQuestionIdsRef.current.add(id);
      setSaveState('idle');
    }
  }, [surveyPublicService]);

  const scheduleAutosave = useCallback(() => {
    if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current);
    autosaveTimerRef.current = setTimeout(() => { flushAnswers(); }, AUTOSAVE_DELAY_MS);
  }, [flushAnswers]);

  useEffect(() => () => {
    if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current);
  }, []);

  const hydrateAnswers = (items: ISurveyResponseItem[]) => {
    const map = new Map<string, ISurveyResponseItem>();
    for (const item of items) {
      if (item.surveyQuestionId) map.set(item.surveyQuestionId, item);
    }
    setAnswers(map);
  };

  const resolveAndStart = useCallback(async (email?: string, name?: string) => {
    if (!surveyData) return;
    try {
      const startBody = inviteToken
        ? { inviteToken }
        : { openLinkToken, respondentEmail: email!, respondentName: name || undefined };

      const session = await surveyPublicService.startSession(startBody);
      setSessionToken(session.sessionToken);
      writeStoredToken(surveyData.anonymous ?? false, session.sessionToken);
      setPhase('questions');
    } catch (err: any) {
      if (err?.response?.status === 422) {
        setPhase('already_submitted');
      } else {
        setErrorMessage('Could not start session. Please try again.');
        setPhase('error');
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [surveyData, inviteToken, openLinkToken]);

  useEffect(() => {
    const init = async () => {
      try {
        const data = await surveyPublicService.resolve({ inviteToken, openLinkToken });
        setSurveyData(data);
        const anonymous = data.anonymous ?? false;

        // Resume an in-progress session if one is stored for this link.
        const storedToken = readStoredToken(anonymous);
        if (storedToken) {
          try {
            const saved = await surveyPublicService.getAnswers(storedToken);
            hydrateAnswers(saved.items ?? []);
            setResumedProductRows(saved.productResponses ?? []);
            setSessionToken(storedToken);
            setPhase('questions');
            return;
          } catch (resumeErr: any) {
            const status = resumeErr?.response?.status;
            const message = String(resumeErr?.response?.data?.message ?? '');
            clearStoredToken();
            if (status === 403 && message.includes('already been submitted')) {
              setPhase('already_submitted');
              return;
            }
            // Any other failure falls through to a fresh start.
          }
        }

        const needsIdentity =
          !inviteToken && (data.distributionMode === 'OPEN_LINK_ONLY' || data.distributionMode === 'BOTH');

        if (needsIdentity) {
          setPhase('identity');
        } else {
          // Auto-start for invite token
          try {
            const session = await surveyPublicService.startSession({ inviteToken });
            setSessionToken(session.sessionToken);
            writeStoredToken(anonymous, session.sessionToken);
            setPhase('questions');
          } catch (err: any) {
            if (err?.response?.status === 422) {
              setPhase('already_submitted');
            } else {
              throw err;
            }
          }
        }
      } catch (err: any) {
        const status = err?.response?.status;
        if (status === 404) setErrorMessage('This survey could not be found.');
        else if (status === 403) setErrorMessage('This survey is not currently active.');
        else if (status === 410) setErrorMessage('This survey link has expired.');
        else setErrorMessage('An unexpected error occurred. Please try again.');
        setPhase('error');
      }
    };
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleIdentitySubmit = () => {
    // Backend stores and matches emails lowercased; normalise so what the respondent sees is what gets stored.
    const normalisedEmail = respondentEmail.trim().toLowerCase();
    if (!normalisedEmail.includes('@')) return;
    resolveAndStart(normalisedEmail, respondentName);
  };

  const handleAnswerChange = (answer: ISurveyResponseItem) => {
    setAnswers(prev => {
      const next = new Map(prev);
      const existing = prev.get(answer.surveyQuestionId);
      next.set(answer.surveyQuestionId, { ...existing, ...answer });
      return next;
    });
    dirtyQuestionIdsRef.current.add(answer.surveyQuestionId);
    setSaveState('idle');
    scheduleAutosave();
  };

  const allItems = (surveyData?.surveyQuestionGroup?.surveyQuestionGroupItems ?? [])
    .slice()
    .sort((a, b) =>
      (a.pageNumber ?? 1) !== (b.pageNumber ?? 1)
        ? (a.pageNumber ?? 1) - (b.pageNumber ?? 1)
        : a.sortOrder - b.sortOrder,
    );

  // All pages that have at least one visible item
  const allPageNumbers = [...new Set(allItems.map(i => i.pageNumber ?? 1))].sort((a, b) => a - b);
  if (allPageNumbers.length === 0) allPageNumbers.push(1);
  const maxPage = allPageNumbers[allPageNumbers.length - 1];

  // A page whose questions are all hidden by conditions (or that has none) is skipped by the
  // pager entirely, so a recipient never lands on "No questions on this page". Recomputed from
  // the live answers: answering differently can reveal or hide later pages.
  const pageHasVisibleItems = (page: number) => allItems.some(item =>
    (item.pageNumber ?? 1) === page && evaluateRule(item.visibilityRule, answers),
  );
  const visiblePageNumbers = allPageNumbers.filter(pageHasVisibleItems);
  const totalVisiblePages = Math.max(visiblePageNumbers.length, 1);
  const displayPageIndex = visiblePageNumbers.includes(currentPage)
    ? visiblePageNumbers.indexOf(currentPage) + 1
    : visiblePageNumbers.filter(p => p < currentPage).length + 1;
  const nextVisiblePage = visiblePageNumbers.find(p => p > currentPage);
  const prevVisiblePage = [...visiblePageNumbers].reverse().find(p => p < currentPage);

  const visibleItems = allItems.filter(item =>
    (item.pageNumber ?? 1) === currentPage && evaluateRule(item.visibilityRule, answers),
  );

  const currentPageRequiredAnswered = visibleItems
    .filter(isItemRequired)
    .every(item => isItemAnswered(item, answers));

  const currentPageInRange = visibleItems.every(item => isItemInRange(item, answers));

  // For final submit: all visible items across all pages
  const allVisibleItems = allItems.filter(item => evaluateRule(item.visibilityRule, answers));
  const outstandingItems = allVisibleItems.filter(item =>
    (isItemRequired(item) && !isItemAnswered(item, answers)) || !isItemInRange(item, answers),
  );

  const firstOffendingPage = (items: ISurveyQuestionGroupItem[]): number =>
    items.reduce((min, item) => Math.min(min, item.pageNumber ?? 1), maxPage);

  const handleSubmit = async () => {
    if (!sessionToken) return;
    if (outstandingItems.length > 0) {
      setShowValidation(true);
      const requiredCount = outstandingItems.length;
      setErrorMessage(`${requiredCount} question${requiredCount === 1 ? '' : 's'} still need${requiredCount === 1 ? 's' : ''} attention before you can submit.`);
      setCurrentPage(firstOffendingPage(outstandingItems));
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    setIsSubmitting(true);
    try {
      // The final submit still posts everything, so autosave is a safety net rather
      // than a dependency.
      const answerList = Array.from(answers.values());
      if (answerList.length > 0) {
        await surveyPublicService.saveAnswers(sessionToken, answerList);
      }
      dirtyQuestionIdsRef.current.clear();
      await surveyPublicService.submitSurvey(sessionToken);
      clearStoredToken();
      setPhase('submitted');
    } catch {
      setErrorMessage('Submission failed. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderContent = () => {
    if (phase === 'loading') {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', pt: 8 }}>
          <LoadingSpinner />
        </Box>
      );
    }

    if (phase === 'error') {
      return (
        <Paper variant='elevation' elevation={0} sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant='h6' color='error' sx={{ mb: 1 }}>Unable to load survey</Typography>
          <Typography color='text.secondary'>{errorMessage}</Typography>
        </Paper>
      );
    }

    if (phase === 'already_submitted') {
      return (
        <Paper variant='elevation' elevation={0} sx={{ p: 4, textAlign: 'center' }}>
          <CheckCircleOutline sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
          <Typography variant='h6'>You have already submitted this survey.</Typography>
          <Typography color='text.secondary' sx={{ mt: 1 }}>Thank you for your response.</Typography>
        </Paper>
      );
    }

    if (phase === 'submitted') {
      return (
        <Paper variant='elevation' elevation={0} sx={{ p: 4, textAlign: 'center' }}>
          <CheckCircleOutline sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
          <Typography variant='h5' sx={{ mb: 1 }}>Thank you!</Typography>
          <Typography color='text.secondary'>Your response has been submitted successfully.</Typography>
        </Paper>
      );
    }

    if (phase === 'questions' && surveyData?.purpose === 'SUPPLIER_PRODUCT_DATA' && sessionToken) {
      return (
        <SurveyProductDataStep
          surveyData={surveyData}
          sessionToken={sessionToken}
          resumedRows={resumedProductRows}
          onSubmitted={() => { clearStoredToken(); setPhase('submitted'); }}
        />
      );
    }

    if (phase === 'identity') {
      const isAnonymous = surveyData?.anonymous ?? false;
      return (
        <Paper variant='elevation' elevation={0} sx={{ p: 4 }}>
          <Typography variant='h6' sx={{ mb: 1 }}>{surveyData?.name}</Typography>
          {surveyData?.description && (
            <Typography color='text.secondary' sx={{ mb: 3 }}>{surveyData.description}</Typography>
          )}
          {isAnonymous && (
            <Box sx={{ mb: 2.5, p: 1.5, bgcolor: 'info.light', borderRadius: 1 }}>
              <Typography variant='body2' color='info.contrastText'>
                This survey is anonymous. Your individual responses cannot be identified by
                administrators; only aggregated results are visible to them.
              </Typography>
            </Box>
          )}
          <Typography variant='subtitle1' sx={{ mb: 2 }}>Please tell us who you are</Typography>
          <Stack spacing={2}>
            <TextField
              label='Email address'
              type='email'
              value={respondentEmail}
              onChange={e => setRespondentEmail(e.target.value)}
              fullWidth
              required
            />
            {!isAnonymous && (
              <TextField
                label='Name (optional)'
                value={respondentName}
                onChange={e => setRespondentName(e.target.value)}
                fullWidth
              />
            )}
            <Button
              variant='contained'
              onClick={handleIdentitySubmit}
              disabled={!respondentEmail.trim().toLowerCase().includes('@')}
            >
              Continue
            </Button>
          </Stack>
        </Paper>
      );
    }

    if (phase === 'questions') {
      const isMultiPage = totalVisiblePages > 1;
      const isLastPage = nextVisiblePage === undefined;

      return (
        <Paper variant='elevation' elevation={0} sx={{ p: 4 }}>
          <Typography variant='h5' sx={{ mb: 0.5 }}>{surveyData?.name}</Typography>
          {surveyData?.description && (
            <Typography color='text.secondary' sx={{ mb: 3 }}>{surveyData.description}</Typography>
          )}
          {surveyData?.invitation?.recipientName && (
            <Typography sx={{ mb: 3 }}>Hi, {surveyData.invitation.recipientName}</Typography>
          )}
          {isMultiPage && (
            <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant='body2' color='text.secondary'>
                Page {displayPageIndex} of {totalVisiblePages}
              </Typography>
              <Box sx={{ flex: 1, height: 4, bgcolor: 'divider', borderRadius: 2, overflow: 'hidden' }}>
                <Box sx={{ height: '100%', width: `${(displayPageIndex / totalVisiblePages) * 100}%`, bgcolor: 'primary.main', transition: 'width 0.3s ease' }} />
              </Box>
            </Box>
          )}
          <Stack spacing={3}>
            {visibleItems.length === 0 ? (
              <Typography color='text.secondary' sx={{ py: 2 }}>
                No questions on this page.
              </Typography>
            ) : (
              visibleItems.map(item => (
                <Box key={item.id}>
                  <Stack spacing={2}>
                    <QuestionInput
                      item={item}
                      answer={answers.get(item.surveyQuestionId)}
                      onChange={handleAnswerChange}
                      showValidation={showValidation}
                    />
                    {item.surveyQuestion?.collectMoreDetails && (
                      <Stack spacing={1}>
                        <Typography
                          id={`${item.surveyQuestion.id}-more-details-heading`}
                          variant='subtitle2'
                          sx={{ fontWeight: 600 }}
                        >
                          Please provide more details:
                        </Typography>
                        <TextField
                          hiddenLabel
                          multiline
                          minRows={2}
                          value={answers.get(item.surveyQuestionId)?.moreDetailsText ?? ''}
                          onChange={e => {
                            const v = e.target.value;
                            handleAnswerChange({
                              surveyQuestionId: item.surveyQuestion!.id,
                              surveyQuestionGroupItemId: item.id,
                              moreDetailsText: v.trim() ? v : null,
                            });
                          }}
                          fullWidth
                          inputProps={{ 'aria-labelledby': `${item.surveyQuestion.id}-more-details-heading` }}
                        />
                      </Stack>
                    )}
                  </Stack>
                </Box>
              ))
            )}
          </Stack>
          <Box sx={{ mt: 4 }}>
            {errorMessage && (
              <Typography color='error' sx={{ mb: 2 }}>{errorMessage}</Typography>
            )}
            <Stack direction='row' justifyContent='space-between' alignItems='center'>
              {isMultiPage && prevVisiblePage !== undefined ? (
                <Button variant='outlined' onClick={() => { flushAnswers(); setCurrentPage(prevVisiblePage); setErrorMessage(''); }}>
                  ← Back
                </Button>
              ) : <span />}
              <Stack direction='row' spacing={2} alignItems='center'>
                {saveState === 'saved' && (
                  <Typography variant='caption' color='text.secondary'>Progress saved</Typography>
                )}
                {saveState === 'saving' && (
                  <Typography variant='caption' color='text.secondary'>Saving…</Typography>
                )}
                {isLastPage ? (
                  <LoadingButton
                    variant='contained'
                    size='large'
                    loading={isSubmitting}
                    onClick={handleSubmit}
                  >
                    Submit
                  </LoadingButton>
                ) : (
                  <Button
                    variant='contained'
                    size='large'
                    disabled={!currentPageRequiredAnswered || !currentPageInRange}
                    onClick={() => { flushAnswers(); setCurrentPage(nextVisiblePage!); setErrorMessage(''); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                  >
                    Next →
                  </Button>
                )}
              </Stack>
            </Stack>
          </Box>
        </Paper>
      );
    }

    return null;
  };

  return (
    <Box sx={{ backgroundColor: '#F0F1F3', minHeight: '100vh' }}>
      <Toolbar />
      <Container maxWidth='sm' sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ mb: 3, display: 'flex', justifyContent: 'center' }}>
          <img
            src={customThemeContext.logoUrl}
            alt='logo'
            height='56'
            style={{ objectFit: 'contain' }}
          />
        </Box>
        {renderContent()}
      </Container>
    </Box>
  );
};
